import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import Order from '../../models/Order';
import PreviewImage from '../PreviewImage';
import Button from '../Button';
import Spinner from '../Spinner';
import Typography from '../Typography';
import { byCameraThenPose, cameraLabelOf } from './cameraSort';
import styles from './FullResDownloads.module.sass';

const getItemKey = (image) => {
  const isPose = image.resourceIdentifier?.type === 'poses';
  return `${isPose ? 'pose' : 'student_image'}-${image.id}`;
};

// One downloadable image, paired with the background + order_package it belongs
// to. Pairing them here (rather than carrying three index-aligned arrays) keeps
// each image bound to its own background/order_package even after we sort.
const poseImageFromSheet = (sheet) => ({
  ...sheet.pose,
  foregroundId: sheet.foregroundId,
  foreground: sheet.foreground,
  isPNG: sheet.pose.isPNG,
  resourceIdentifier: { type: 'poses' },
});

const buildItemsFromImages = (images) => images.map((image) => {
  const isPose = image.resourceIdentifier?.type === 'poses';
  return { type: isPose ? 'pose' : 'student_image', id: image.id };
});

const getDownloadUrl = (image, orderPackage, background, email) => {
  if (!image) return '';

  const isPose = image.resourceIdentifier?.type === 'poses';

  if (isPose) {
    const baseUrl = image.isPNG && image.isPNG() ? '/download/download_png_image' : '/download/download_image';
    return `${baseUrl}/${image.studentImage.studentId}?pose_id=${image.id}&background_id=${background && background.id}&order_package_id=${orderPackage.id}&email=${encodeURIComponent(email)}${image.foregroundId ? `&foreground_id=${image.foregroundId}` : ''}`;
  }
  const baseUrl = image.isPNG && image.isPNG() ? '/download/download_png_image' : '/download/download_image';
  return `${baseUrl}/${image.studentId}?student_image_id=${image.id}${background ? `&background_id=${background.id}` : ''}&order_package_id=${orderPackage.id}&email=${encodeURIComponent(email)}`;
};

export default function FullResDownloads({ orderId, email }) {
  // Each entry is { image, background, orderPackage }, sorted by camera then pose.
  const [items, setItems] = useState([]);
  const [availability, setAvailability] = useState({});
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const pollingRef = useRef(null);

  const checkAvailability = useCallback((availabilityItems) => {
    fetch('/download/check_image_availability', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ order_id: orderId, email, items: availabilityItems }),
    })
      .then((res) => res.json())
      .then((data) => {
        setAvailability(data.availability || {});
        setIsCheckingAvailability(false);

        const allReady = Object.values(data.availability || {}).every((v) => v === true);
        if (allReady && pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      })
      .catch(() => {
        setIsCheckingAvailability(false);
      });
  }, [orderId, email]);

  // Load full order data with sheets/poses
  useEffect(() => {
    Order
      .includes({
        cart: {
          orderPackages: [
            { addonSheets: [{ pose: 'studentImage' }, 'background'] },
            'background',
            'package',
            'student',
            'studentImage',
            { sheets: ['studentImage', 'background', 'foreground', { pose: 'studentImage' }] },
            { api_option: 'image_types' },
            { extras: 'prices' },
          ],
        },
      })
      .where({ for_email: [orderId, email, true] })
      .first()
      .then((response) => {
        if (!response.data) {
          setHasLoaded(true);
          return;
        }

        const purchasedItems = [];

        response.data.cart.orderPackages.forEach((orderPackage) => {
          if (orderPackage.hasDownloadableImages) {
            const downloadableSheets = orderPackage.sheets.filter((sheet) => sheet.hasDownloadableImage);
            // When none are flagged downloadable, fall back to the first sheet
            // (preserves the prior behavior for those packages).
            const sheets = downloadableSheets.length > 0
              ? downloadableSheets
              : orderPackage.sheets.slice(0, 1);

            sheets.forEach((sheet) => {
              if (sheet.pose) {
                purchasedItems.push({ image: poseImageFromSheet(sheet), background: sheet.background, orderPackage });
              }
              if (sheet.studentImage) {
                purchasedItems.push({ image: sheet.studentImage, background: sheet.background, orderPackage });
              }
            });

            orderPackage.addonSheets
              .filter((addonSheet) => addonSheet.hasDownloadableImage)
              .forEach((addonSheet) => {
                purchasedItems.push({ image: addonSheet.pose, background: addonSheet.background, orderPackage });
              });
          }

          if (orderPackage.studentImage && orderPackage.studentImage.imageUrl && orderPackage.hasDownloadableImages) {
            purchasedItems.push({ image: orderPackage.studentImage, background: orderPackage.background, orderPackage });
          }
        });

        // Group by camera, ordered by pose within each camera. Stamp a stable
        // uid per item for React keys (the same image can recur across packages).
        purchasedItems.sort(byCameraThenPose);

        setItems(purchasedItems.map((it, idx) => ({ ...it, uid: `${getItemKey(it.image)}-${idx}` })));
        setHasLoaded(true);
      });
  }, [orderId, email]);

  // Start availability checking and polling once images are loaded
  useEffect(() => {
    if (items.length === 0) return undefined;

    const availabilityItems = buildItemsFromImages(items.map((it) => it.image));
    checkAvailability(availabilityItems);

    pollingRef.current = setInterval(() => {
      checkAvailability(availabilityItems);
    }, 30000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [items, checkAvailability]);

  if (!hasLoaded) {
    return (
      <div className={styles.container}>
        <div style={{ textAlign: 'center', padding: '20px' }}><Spinner /></div>
      </div>
    );
  }

  if (items.length === 0) return null;

  const readyCount = Object.values(availability).filter((v) => v === true).length;
  const totalCount = items.length;
  const allReady = totalCount > 0 && readyCount === totalCount;

  let progressContent;
  if (isCheckingAvailability) {
    progressContent = <div className={styles.progressLabel}>Checking image availability...</div>;
  } else if (allReady) {
    progressContent = <div className={styles.allReady}>All images ready for download</div>;
  } else {
    progressContent = (
      <>
        <div className={styles.progressLabel}>{`${readyCount} of ${totalCount} images ready`}</div>
        <div className={styles.progressTrack}>
          <div
            className={styles.progressFill}
            style={{ width: `${(readyCount / totalCount) * 100}%` }}
          />
        </div>
        <div className={styles.progressHint}>This page will automatically check for new images every 30 seconds.</div>
      </>
    );
  }

  return (
    <div className={styles.container}>
      <Typography align="center" classes={styles.header}>
        Your Digital Downloads
      </Typography>

      <div className={styles.progressContainer}>{progressContent}</div>

      {isCheckingAvailability && <div style={{ textAlign: 'center', padding: '20px' }}><Spinner /></div>}

      {!isCheckingAvailability && (
        <div className={styles.imageGrid}>
          {items.map((item) => {
            const { image, background, orderPackage } = item;
            const itemKey = getItemKey(image);
            const isReady = availability[itemKey] === true;
            const downloadUrl = getDownloadUrl(image, orderPackage, background, email);
            const label = cameraLabelOf(image);

            return (
              <div key={item.uid} className={styles.imageItem}>
                {label && <div className={styles.caption}>{label}</div>}
                <div className={styles.previewWrapper}>
                  <PreviewImage
                    actualForegroundUrl={image.foregroundId ? image.foreground?.imageUrl : undefined}
                    foregroundUrl={image.imageUrl}
                    backgroundUrl={background ? background.imageUrl : ''}
                    accessCodeOnly={false}
                  />
                </div>
                {isReady ? (
                  <a style={{ display: 'block' }} href={downloadUrl}>
                    <Button fullWidth variant="primary">
                      Download Image
                    </Button>
                  </a>
                ) : (
                  <Button fullWidth variant="primary" disabled>
                    Preparing Image...
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

FullResDownloads.propTypes = {
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  email: PropTypes.string.isRequired,
};
